// ╔══════════════════════════════════════════════════════════════════════╗
// ║ Brand configuration — the SINGLE source of truth for the product name, ║
// ║ wordmark, symbol, domain, e-mail identity and brand colours.           ║
// ╚══════════════════════════════════════════════════════════════════════╝
// The values live in ./brand.json (so UI, Vite config and Node tooling can all
// read the same file); this module adds types + derived helpers. Everything
// brand-related (UI logo, PDF labels, app/tab titles, PWA manifest,
// favicon/icon generation, transactional e-mail from-identity) reads from here.
//
// There are two visible brands: the company product "Care & Key" (back-office +
// technician app) and the owner portal "Ownerward" (by Care & Key). The portal
// overrides name / wordmark / symbol via brand.json's `portal` block; pass
// kind: 'portal' to the helpers and the Logo to render it.
import brand from './brand.json' with { type: 'json' };

export type BrandColors = {
  /** dark ink — text on light, square fill on light, symbol fill on dark */
  ink: string;
  /** paper white — text on dark, square fill on dark, symbol fill on light */
  paper: string;
  accent: string;
  accentDark: string;
};

export type BrandKind = 'main' | 'portal';
export type Wordmark = { prefix: string; accent: string };

export const BRAND = {
  /** Product name — titles, PWA name, e-mail from-name. */
  name: brand.name,
  /** Wordmark: one word, two weights, no separator — `prefix` light, `accent` bold. */
  wordmark: brand.wordmark as Wordmark,
  /** The glyph(s) in the square symbol (and generated icons). */
  symbolLetter: brand.symbolLetter,
  /** Tracking applied to the wordmark. */
  letterSpacing: brand.letterSpacing,
  /** Suffix after the name per app (tab/PWA titles). Empty = just the name. */
  appSuffix: brand.appSuffix as Record<'web' | 'field' | 'portal', string>,
  domain: brand.domain,
  email: {
    fromAddress: `no-reply@${brand.domain}`,
    fromName: brand.name,
  },
  colors: brand.colors as BrandColors,
  /** The owner portal's own visible identity (by Care & Key). */
  portal: {
    name: brand.portal.name,
    tagline: brand.portal.tagline,
    wordmark: brand.portal.wordmark as Wordmark,
    symbolLetter: brand.portal.symbolLetter,
  },
} as const;

/** The visible name, wordmark and symbol for a brand kind. */
export function brandFor(kind: BrandKind = 'main') {
  return kind === 'portal'
    ? { name: BRAND.portal.name, wordmark: BRAND.portal.wordmark, symbolLetter: BRAND.portal.symbolLetter, tagline: BRAND.portal.tagline }
    : { name: BRAND.name, wordmark: BRAND.wordmark, symbolLetter: BRAND.symbolLetter, tagline: '' };
}

/** Full tab/PWA title for an app. The portal shows its own name (Ownerward). */
export function appTitle(app: 'web' | 'field' | 'portal' = 'web'): string {
  if (app === 'portal') return BRAND.portal.name;
  const suffix = BRAND.appSuffix[app];
  return suffix ? `${BRAND.name} ${suffix}` : BRAND.name;
}
